import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Table2, BookOpen, ArrowUpRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { freeFetch, formatDate, parseWeight, BUILD_STAMP, GRIP_LABELS, SESSION_TYPE_PLATEAU_LABELS } from './api';
import { InAppBrowserNotice, InstallPromptBar } from './InstallHints';
import WeeklyCheckIn from './WeeklyCheckIn';

export default function FreeDashboard() {
  const [me, setMe] = useState(null);
  const [showReengagement, setShowReengagement] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [editingBw, setEditingBw] = useState(false);
  const [bwValue, setBwValue] = useState('');
  const [bwError, setBwError] = useState('');
  const [conversionMoment] = useState(() => {
    const flag = sessionStorage.getItem('free_six_trigger_moment');
    sessionStorage.removeItem('free_six_trigger_moment');
    return Boolean(flag);
  });

  const loadMe = () => freeFetch('/me').then(setMe).catch(() => {});

  useEffect(() => {
    freeFetch('/me')
      .then((data) => {
        setMe(data);
        if (data.show_reengagement) {
          setShowReengagement(true);
          freeFetch('/reengagement/ack', { method: 'POST' }).catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  const saveBodyweight = async () => {
    const value = parseWeight(bwValue);
    if (Number.isNaN(value) || value < 30 || value > 250) {
      setBwError('Enter a bodyweight between 30 and 250 kg.');
      return;
    }
    setBwError('');
    try {
      await freeFetch('/bodyweight', { method: 'PUT', body: JSON.stringify({ bodyweight_kg: value }) });
      setEditingBw(false);
      setMe((prev) => (prev ? { ...prev, bodyweight_kg: value } : prev));
      loadMe();
    } catch (err) {
      setBwError(err.message);
    }
  };

  const joinWaitlist = async () => {
    setJoiningWaitlist(true);
    try {
      await freeFetch('/waitlist/join', { method: 'POST' });
      setMe((prev) => (prev ? { ...prev, waitlist_joined: true, priority_access: true } : prev));
    } catch (err) {
      // Non-blocking: still show confirmation, retry silently happens on next click.
    } finally {
      setJoiningWaitlist(false);
      setWaitlistOpen(true);
    }
  };

  if (!me) {
    return <p className="text-mono-mute font-body" data-testid="free-dashboard-loading">Loading...</p>;
  }

  return (
    <div className="space-y-8" data-testid="free-dashboard">
      <InAppBrowserNotice />
      <InstallPromptBar sessionCount={me.session_count} />

      {showReengagement && (
        <div className="border border-mono-line bg-mono-card p-6" data-testid="free-reengagement-banner">
          <p className="text-white font-body leading-relaxed mb-1">You have not logged in 10 days.</p>
          <p className="text-mono-mute font-body leading-relaxed mb-4">
            No lecture. Just a reminder that the sessions you do not log are the ones that do not compound.
          </p>
          <p className="text-mono-mute font-body mb-4">Whenever you are ready —</p>
          <Link
            to="/free/log"
            className="inline-block bg-white hover:bg-[#E5E5E5] text-black font-heading font-bold uppercase tracking-wider rounded-none px-6 py-2.5 transition-colors"
            data-testid="free-reengagement-log-btn"
          >
            Log session
          </Link>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-none">
            Dashboard
          </h1>
          <p className="text-mono-mute font-body text-sm mt-3" data-testid="free-status-line">
            Sessions logged: <span className="text-white font-semibold">{me.session_count}</span>
            <span className="text-mono-line mx-2">|</span>
            Started: <span className="text-white font-semibold">{formatDate(me.date_joined)}</span>
            <span className="text-mono-line mx-2">|</span>
            Current streak:{' '}
            <span className="text-white font-semibold" data-testid="free-streak">
              {me.current_streak} {me.current_streak === 1 ? 'day' : 'days'}
            </span>
          </p>
          {editingBw ? (
            <div className="flex items-center gap-2 mt-2" data-testid="free-bodyweight-edit">
              <input
                type="text"
                inputMode="text"
                value={bwValue}
                onChange={(e) => setBwValue(e.target.value)}
                className="w-28 bg-mono-card border border-mono-line text-white rounded-none px-2 py-1.5 font-body free-input-text focus:outline-none focus:border-white transition-colors"
                data-testid="free-bodyweight-input"
              />
              <button
                onClick={saveBodyweight}
                className="bg-white hover:bg-[#E5E5E5] text-black font-heading text-xs font-bold uppercase tracking-wider rounded-none px-3 py-1.5 transition-colors"
                data-testid="free-bodyweight-save"
              >
                Save
              </button>
              <button
                onClick={() => { setEditingBw(false); setBwError(''); }}
                className="text-mono-mute hover:text-white font-body text-xs underline transition-colors"
                data-testid="free-bodyweight-cancel"
              >
                Cancel
              </button>
            </div>
          ) : (
            <p className="text-mono-mute font-body text-sm mt-2" data-testid="free-bodyweight-line">
              Bodyweight: {me.bodyweight_kg != null ? `${me.bodyweight_kg} kg` : 'not set'} —{' '}
              <button
                onClick={() => { setBwValue(me.bodyweight_kg != null ? String(me.bodyweight_kg) : ''); setEditingBw(true); }}
                className="underline hover:text-white transition-colors"
                data-testid="free-bodyweight-edit-btn"
              >
                {me.bodyweight_kg != null ? 'edit' : 'add'}
              </button>
            </p>
          )}
          {bwError && <p className="text-red-400 font-body text-xs mt-1" data-testid="free-bodyweight-error">{bwError}</p>}
        </div>
        <button
          onClick={joinWaitlist}
          disabled={joiningWaitlist}
          className="border border-crimson text-crimson hover:bg-crimson hover:text-white font-heading font-bold text-sm uppercase tracking-wider rounded-none px-5 py-2.5 transition-colors flex items-center gap-1.5 disabled:opacity-60"
          data-testid="free-waitlist-btn"
        >
          Join the waitlist <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>

      {me.plateau_type && !conversionMoment && !showReengagement && (
        <div className="bg-mono-card border-l-4 border-crimson rounded-none p-6" data-testid="free-plateau-card">
          <p className="text-crimson font-heading text-xs font-bold uppercase tracking-[0.2em] mb-2">
            {me.plateau_grip && me.plateau_grip !== 'pullup'
              ? `Plateau · ${GRIP_LABELS[me.plateau_grip]}`
              : 'Plateau detected'}
          </p>
          <p className="text-white font-body leading-relaxed">
            Same weight for 3 {SESSION_TYPE_PLATEAU_LABELS[me.plateau_type] || me.plateau_type} sessions running.
          </p>
          <p className="text-white font-body leading-relaxed mt-3">
            Coach Rands' advice — before you change anything in the gym, make sure your sleep and nutrition are on
            point. If they are, keep working at this weight. Control the descent for a full 3 seconds on every rep,
            and on your last rep of the last set, hold at the top as long as you can.
          </p>
        </div>
      )}

      {me.priority_access && (
        <div className="border border-crimson bg-crimson/5 px-4 py-2.5 inline-block" data-testid="free-priority-banner">
          <span className="text-crimson font-heading text-xs font-bold uppercase tracking-[0.2em]">
            Priority access: confirmed
          </span>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Link
          to="/free/log"
          className="md:col-span-2 bg-crimson hover:bg-crimson-bright transition-colors p-8 group"
          data-testid="free-dash-log-session"
        >
          <ClipboardList className="h-7 w-7 text-white/80 mb-4" />
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-white">Log Session</h2>
          <p className="text-white/70 font-body text-sm mt-1">Record today's session. Takes under a minute.</p>
        </Link>

        <Link
          to="/free/progress"
          className="border border-mono-line hover:border-mono-mute bg-mono-card transition-colors p-8"
          data-testid="free-dash-progress"
        >
          <Table2 className="h-6 w-6 text-mono-mute mb-4" />
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-white">My Progress</h2>
          <p className="text-mono-mute font-body text-sm mt-1">Every session you have logged, in one table.</p>
        </Link>

        <Link
          to="/free/technique"
          className="border border-mono-line hover:border-mono-mute bg-mono-card transition-colors p-8"
          data-testid="free-dash-technique"
        >
          <BookOpen className="h-6 w-6 text-mono-mute mb-4" />
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-white">Technique Guide</h2>
          <p className="text-mono-mute font-body text-sm mt-1">The 5 pull-up cues, written out as a reference.</p>
        </Link>
      </div>

      <WeeklyCheckIn me={me} onSubmitted={loadMe} />

      <p className="text-mono-line font-mono text-[10px] text-center pt-2" data-testid="free-build-stamp">
        {BUILD_STAMP}
      </p>

      <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
        <DialogContent className="bg-mono-card border-mono-line rounded-none" data-testid="free-waitlist-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-wider text-white">
              You're on the list
            </DialogTitle>
          </DialogHeader>
          <p className="text-white font-body leading-relaxed" data-testid="free-waitlist-confirmation-text">
            You're on the priority list. We'll email <span className="font-semibold">{me.email}</span> when the full
            program opens.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
